// Feed.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import { useAuth } from './AuthContext';
import { useFollowManager } from './FollowManager';
import { useSavedPosts } from './SavedPostsManager';
import { COUNTRIES } from './config';
import Post from './Post';
import FloatingPostButton from './FloatingPostButton';

const Feed = ({ relays }) => {
  const { translate, country } = useLanguage();
  const { publicKey } = useAuth();
  const { following } = useFollowManager(relays);
  const { savedPosts } = useSavedPosts();
  const countryConfig = COUNTRIES[country];
  
  const [activeTab, setActiveTab] = useState('all');
  const [posts, setPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  
  const loadedEvents = useRef(new Set());
  const searchTimeout = useRef(null);
  const searchIndex = useRef({});

  // Create regex for government domains based on country  
	const createGovRegex = useCallback(() => {
	  const domains = countryConfig.govDomains.map(domain => {
		const escapedDomain = domain.replace('.', '\\.');
		return `${escapedDomain}(?:/|$)`;
	  }).join('|');
	  return new RegExp(`(https?:\\/\\/[^\\s]+(?<!loc\\.)(${domains})[^\\s]*)`);
	}, [countryConfig.govDomains]);
  
  // Fetch liked posts
	useEffect(() => {
	  if (publicKey) {
		const fetchLikedPosts = async () => {
		  const likedSet = new Set();
		  
		  for (const relay of relays) {
			const ws = new WebSocket(relay);
			
			try {
			  await new Promise((resolve) => {
				const timeout = setTimeout(() => {
				  ws.close();
				  resolve();
				}, 5000);
  
				ws.onopen = () => {
				  ws.send(JSON.stringify([
					"REQ",
					"liked-posts",
					{
					  kinds: [7], // Reaction events
					  authors: [publicKey],
					  limit: 500
					}
				  ]));
				};
  
				ws.onmessage = (event) => {
				  try {
					const [type, , data] = JSON.parse(event.data);
					if (type === "EVENT" && data.kind === 7 && data.content === "+") {
					  // Extract post ID from tags
					  const postTag = data.tags.find(tag => tag[0] === "e");
					  if (postTag && postTag[1]) {
						likedSet.add(postTag[1]);
					  }
					}
					if (type === "EOSE") {
					  clearTimeout(timeout);
					  ws.close();
					  resolve();
					}
				  } catch (e) {
					console.error('Failed to parse liked posts:', e);
				  }
				};
  
				ws.onerror = () => {
				  clearTimeout(timeout);
				  resolve();
				};
			  });
			} catch (error) {
			  console.error('Error fetching liked posts:', error);
			}
		  }
		  
		  setLikedPosts(likedSet);
		};
  
		fetchLikedPosts();
	  }
	}, [publicKey, relays]);

	// Filter posts based on active tab
  const getFilteredPosts = useCallback(() => {
		let filtered = [...posts];
		
		// Apply search filter if exists
		if (searchTerm) {
		  filtered = filtered.filter(post => 
			post.content.toLowerCase().includes(searchTerm.toLowerCase())
		  );
		}
		
		// Apply tab filters
		switch (activeTab) {
		  case 'following':
			filtered = filtered.filter(post => following.has(post.pubkey));
			break;
		  case 'saved':
			filtered = filtered.filter(post => savedPosts.has(post.id));
			break;
		  case 'likes':
			filtered = filtered.filter(post => likedPosts.has(post.id));
			break;
		  default: // 'all'
			break;
		}
		
		return filtered;
		  }, [posts, activeTab, following, savedPosts, likedPosts, searchTerm]);

  // Index post content for search
  const indexPost = useCallback((post) => {
    const words = post.content.toLowerCase().split(/\s+/);
    words.forEach(word => {
      if (!searchIndex.current[word]) {
        searchIndex.current[word] = new Set();
      }
      searchIndex.current[word].add(post.id);
    });
  }, []);

  // Check if a URL is a valid government URL
  const isValidGovUrl = useCallback((url) => {
    // Exclude loc.gov
    if (url.includes('loc.gov')) return false;
    
    // Include govglance.org
    if (url.includes('govglance.org')) return true;
    
    // Check for valid government domains
    return countryConfig.govDomains.some(domain => {
      const escapedDomain = domain.replace('.', '\\.');
      const validDomainRegex = new RegExp(`${escapedDomain}(?:/|$)`);
      return validDomainRegex.test(url);
    });
  }, [countryConfig.govDomains]);

  // Process incoming event
  const processEvent = useCallback((event) => {
    if (loadedEvents.current.has(event.id)) return;
    
    const govRegex = createGovRegex();
    if (!govRegex.test(event.content)) return;
    
    // Extract all URLs from the content
    const urlRegex = /https?:\/\/[^\s]+/g;
    const urls = event.content.match(urlRegex) || [];
    
    // Check if any URL is a valid government URL
    const hasValidGovUrl = urls.some(isValidGovUrl);
    
    if (hasValidGovUrl) {
      loadedEvents.current.add(event.id);
      indexPost(event);
      setPosts(prevPosts => {
        const newPosts = [...prevPosts, event];
        return newPosts.sort((a, b) => b.created_at - a.created_at);
      });
    }
  }, [createGovRegex, indexPost, isValidGovUrl]);

  // Connect to relays and subscribe to posts
  useEffect(() => {
    if (!relays || relays.length === 0) {
      setError('No relays configured');
      setLoading(false);
      return;
    }

    setPosts([]);
    loadedEvents.current = new Set();
    searchIndex.current = {};
    setLoading(true);
    setError('');

    const websockets = [];
    let completedRelays = 0;

    for (const url of relays) {
      try {
        const ws = new WebSocket(url);
        websockets.push(ws);
        
        const subId = `feed-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        ws.onopen = () => {
          // Subscribe to kind 1 events from the last year
          ws.send(JSON.stringify([
            "REQ",
            subId,
            {
              kinds: [1],
              limit: 1000,
              since: Math.floor(Date.now() / 1000) - 365 * 24 * 60 * 60
            }
          ]));
        };

        ws.onmessage = (msg) => {
          try {
            const data = JSON.parse(msg.data);
            const [type, , eventData] = data;
            
            if (type === "EVENT" && eventData) {
              processEvent(eventData);
            }
            
            if (type === "EOSE") {
              completedRelays++;
              if (completedRelays >= relays.length) {
                setLoading(false);
              }
            }
          } catch (e) {
            // Ignore parse errors
          }
        };

        ws.onerror = () => {
          completedRelays++;
          if (completedRelays >= relays.length && websockets.every(w => w.readyState !== WebSocket.OPEN)) {
            setError('Failed to connect to any relays');
            setLoading(false);
          }
        };

      } catch (err) {
        console.error(`Failed to connect to ${url}:`, err);
      }
    }

    // Cleanup function
    return () => {
      websockets.forEach(ws => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      });
    };
  }, [relays, processEvent]);

  const filteredPosts = getFilteredPosts();
  
  return (
    <div className="feed" role="region" aria-label="Posts feed">
      <div className="search-section">
        <label htmlFor="search-input" className="visually-hidden">
          Search posts
        </label>
        <input
          id="search-input"
          type="search"
          placeholder={translate('search')}
          onChange={(e) => {
            if (searchTimeout.current) {
              clearTimeout(searchTimeout.current);
            }
            searchTimeout.current = setTimeout(() => {
              setSearchTerm(e.target.value);
            }, 300);
          }}
          className="search-bar"
          aria-describedby="results-count"
        />
      </div>
      
      <nav className="feed-tabs" role="tablist" aria-label="Feed filters">
        <button 
          className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          role="tab"
          aria-selected={activeTab === 'all'}
          aria-controls="post-list"
        >
          All
        </button>
        <button 
          className={`tab-button ${activeTab === 'following' ? 'active' : ''}`}
          onClick={() => setActiveTab('following')}
          role="tab"
          aria-selected={activeTab === 'following'}
          aria-controls="post-list"
        >
          Following
        </button>
        <button 
          className={`tab-button ${activeTab === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved')}
          role="tab"
          aria-selected={activeTab === 'saved'}
          aria-controls="post-list"
        >
          Saved
        </button>
        <button 
          className={`tab-button ${activeTab === 'likes' ? 'active' : ''}`}
          onClick={() => setActiveTab('likes')}
          role="tab"
          aria-selected={activeTab === 'likes'}
          aria-controls="post-list"
        >
          Likes
        </button>
      </nav>
    
      {error && (
        <div className="error-message" role="alert" aria-live="polite">
          {error}
        </div>
      )}
      
      <p 
        id="results-count" 
        className="results-count" 
        aria-live="polite"
        aria-atomic="true"
      >
        {translate('foundPosts', { count: filteredPosts.length })}
      </p>
    
      <div 
        id="post-list"
        className="post-list" 
        role="feed" 
        aria-busy={loading}
        aria-label={`${activeTab} posts`}
      >
        {loading && filteredPosts.length === 0 ? (
          <div className="loading" role="status" aria-live="polite">
            <span className="visually-hidden">Loading posts...</span>
            {translate('loading')}
          </div>
        ) : filteredPosts.length > 0 ? (
          filteredPosts.map((post, index) => (
            <Post 
              key={post.id}
              post={post} 
              relays={relays}
              aria-setsize={filteredPosts.length}
              aria-posinset={index + 1}
            />
          ))
        ) : (
          <p role="status" aria-live="polite">{translate('noResults')}</p>
        )}
        
        {loading && filteredPosts.length > 0 && (
          <div className="loading" role="status" aria-live="polite">
            <span className="visually-hidden">Loading more posts...</span>
            {translate('loading')}
          </div>
        )}
      </div>
      <FloatingPostButton relays={relays} />
    </div>
  );
};

  
  export default Feed;